import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  Button,
  Paper,
  Grid,
  Card,
  IconButton,
  Chip,
  Pagination,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Visibility from '@mui/icons-material/Visibility';
import Search from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import DonutLargeIcon from '@mui/icons-material/DonutLarge';
import {
  getAllPostSeasonActivitiesAction,
  getAllDistrictsAction,
  getAllMandalsAction,
  getAllSeasonsAction,
  getAllZonesAction,
  getAllRegionsAction,
  getAllAreasAction,
  getAllUserAction,
} from '../../../store/actions';
import { useDispatch, useSelector } from 'react-redux';
import PostActivityDetailsView from './PostActivityDetailsView';

const PostActivityAMRM = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.only('xs'));
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { successData } = useSelector((state) => state.getAllPostSeasonActivitiesReducer);
  const { successData: districtsData } = useSelector((state) => state.getAllDistrictsReducer);
  const { successData: mandalsData } = useSelector((state) => state.getAllMandalsReducer);
  const { successData: seasonsData } = useSelector((state) => state.getAllSeasonsReducer);
  const { successData: zonesData } = useSelector((state) => state.getAllZonesReducer || {});
  const { successData: regionsData } = useSelector((state) => state.getAllRegionsReducer || {});
  const { successData: areasData } = useSelector((state) => state.getAllAreasReducer || {});
  const { successData: usersData } = useSelector((state) => state.getAllUsersReducer || {});
  const Zones = zonesData?.data?.zones || [];
  const Regions = regionsData?.data?.regions || [];
  const Areas = areasData?.data?.areas || [];
  const Users = usersData?.data?.users || [];

  const District = districtsData?.data?.districts || [];
  const Mandal = mandalsData?.data?.mandals || [];
  const Seasons = seasonsData?.data?.seasons || [];

  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [viewRow, setViewRow] = useState(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 30;

  const gridColsDesktop = '0.9fr 1.6fr 1.2fr 1.2fr 1.2fr 0.9fr 0.8fr 1fr';
  const isIpad = useMediaQuery('(min-width:768px) and (max-width:1024px)');

  const rows = successData?.data || [];
  const statsSummary = useMemo(() => {
    const total = rows.length;
    const active = rows.filter((row) => row.is_active).length;
    const inactive = total - active;
    const target = rows.reduce(
      (sum, row) => sum + Number(row.dv_count || 0) + Number(row.fd_count || 0) + Number(row.fv_count || 0),
      0
    );
    const done = rows.reduce(
      (sum, row) => sum + Number(row.dv_done_count || 0) + Number(row.fd_done_count || 0) + Number(row.fv_done_count || 0),
      0
    );
    const completionRate = target > 0 ? Math.round((done / target) * 100) : 0;

    return { total, active, inactive, completionRate };
  }, [rows]);

  const stats = [
    { label: 'TOTAL TARGETS', value: String(statsSummary.total), icon: <TrackChangesIcon />, bg: '#EEF4FF' },
    { label: 'ACTIVE TARGETS', value: String(statsSummary.active), icon: <CheckCircleIcon />, bg: '#E6FBF4' },
    { label: 'INACTIVE TARGETS', value: String(statsSummary.inactive), icon: <CancelIcon />, bg: '#FFF1F1' },
    { label: 'COMPLETION RATE', value: `${statsSummary.completionRate}%`, icon: <DonutLargeIcon />, bg: '#F4F3FF' },
  ];

  const handleAddTarget = () => {
    navigate('/amrm/postActivity/create');
  };

  const handleView = (activity) => {
    setViewRow(activity);
    setOpenViewDialog(true);
  };

  const handleCloseViewDialog = () => {
    setOpenViewDialog(false);
    setViewRow(null);
  };

  const handleEdit = (activity) => {
    navigate('/amrm/postActivity/create', { state: { mode: 'edit', activity } });
  };

  // useEffect(() => {
  //   dispatch(getAllPostSeasonActivitiesAction());
  //   dispatch(getAllDistrictsAction());
  //   dispatch(getAllMandalsAction());
  //   dispatch(getAllSeasonsAction());
  //   dispatch(getAllZonesAction());
  //   dispatch(getAllRegionsAction());
  //   dispatch(getAllAreasAction());
  //   dispatch(getAllUserAction());
  // }, [dispatch]);

  const [search, setSearch] = useState('');
  const [locationType, setLocationType] = useState('All');
  const [districtId, setDistrictId] = useState('All');
  const [mandalName, setMandalName] = useState('All');
  const [seasonId, setSeasonId] = useState('All');
  const [zoneId, setZoneId] = useState('All');
  const [regionId, setRegionId] = useState('All');
  const [areaId, setAreaId] = useState('All');
  const [userName, setUserName] = useState('All');

  // Use id state directly for filters
  const selectedDistrictId = districtId !== 'All' ? districtId : undefined;
  const selectedZoneId = zoneId !== 'All' ? zoneId : undefined;
  const selectedRegionId = regionId !== 'All' ? regionId : undefined;
  const selectedAreaId = areaId !== 'All' ? areaId : undefined;
  const selectedSeasonId = seasonId !== 'All' ? seasonId : undefined;
  const selectedUserId = useMemo(
    () => (userName !== 'All' ? Users.find((u) => u.name === userName)?.id : undefined),
    [userName, Users]
  );

  const getAreaName = (row) => {
    if (!row || !row.target_area_type || !row.target_area_id) return '';

    if (row.target_area_type.toLowerCase() === 'mandal') {
      const mandal = Mandal.find((m) => String(m.mandalId) === String(row.target_area_id));
      if (mandal) {
        return `${mandal.districtName || ''} - ${mandal.name || ''}`;
      }
      return row.target_area_id;
    }

    if (row.target_area_type.toLowerCase() === 'district') {
      const district = District.find((d) => String(d.districtId) === String(row.target_area_id));
      if (district) {
        return district.name || '';
      }
      return row.target_area_id;
    }

    return row.target_area_id;
  };

  const getSeasonDisplayName = (row) => {
    if (row.season && (row.season.name || row.season.season_name)) {
      const name = row.season.name || row.season.season_name;
      const year = row.season.year || row.season.season_year || '';
      return year ? `${name} (${year})` : name;
    }
    const seasonId = row.seasonId ?? row.season_id;
    const found = Seasons.find((s) => String(s.seasonId || s.id) === String(seasonId));
    if (found) {
      const name = found.name || found.season_name;
      const year = found.year || found.season_year || '';
      return year ? `${name} (${year})` : name;
    }
    return row.seasonId ?? row.season_id;
  };

  const filteredData = useMemo(() => {
    const norm = (v) => String(v ?? '').toLowerCase();
    return rows.filter((row) => {
      const seasonId = row.seasonId ?? row.season_id ?? row?.season?.seasonId;
      const seasonNameVal = row?.season?.name;
      if (search.trim()) {
        const q = norm(search.trim());
        const matches =
          norm(seasonNameVal).includes(q) ||
          norm(row.target_area_type).includes(q) ||
          norm(row.dv_count).includes(q) ||
          norm(row.fd_count).includes(q) ||
          norm(row.fv_count).includes(q);
        if (!matches) return false;
      }
      if (selectedUserId !== undefined && String(row.user_id ?? row.userId) !== String(selectedUserId)) return false;
      return true;
    });
  }, [rows, search, selectedUserId]);

  useEffect(() => { setPage(1); }, [search, locationType, districtId, zoneId, regionId, areaId, seasonId, userName]);

  const totalPages = Math.max(1, Math.ceil(filteredData.length / PAGE_SIZE));
  const pagedData = filteredData.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

   // Fetch master data only once on mount
    useEffect(() => {
        dispatch(getAllMandalsAction());
        dispatch(getAllDistrictsAction());
        dispatch(getAllZonesAction());
        dispatch(getAllRegionsAction());
        dispatch(getAllAreasAction());
        dispatch(getAllSeasonsAction());
    }, [dispatch]);

    // Fetch post-season activities when filters change
    useEffect(() => {
        const queryParams = {};
        if (selectedZoneId) queryParams.zoneId = selectedZoneId;
        if (selectedRegionId) queryParams.regionId = selectedRegionId;
        if (selectedAreaId) queryParams.areaId = selectedAreaId;
        if (selectedDistrictId) queryParams.districtId = selectedDistrictId;
        if (selectedSeasonId) queryParams.seasonId = selectedSeasonId;
        // For debugging
        console.log('Query Params sent to backend:', queryParams);
        const paramsArr = [];
        if (selectedSeasonId) paramsArr.push(`seasonId=${selectedSeasonId}`);
        if (selectedZoneId) paramsArr.push(`zoneId=${selectedZoneId}`);
        if (selectedRegionId) paramsArr.push(`regionId=${selectedRegionId}`);
        if (selectedAreaId) paramsArr.push(`areaId=${selectedAreaId}`);
        if (selectedDistrictId) paramsArr.push(`districtId=${selectedDistrictId}`);
        const query = paramsArr.length ? `target/post-season?${paramsArr.join('&')}` : 'target/post-season';
        dispatch(getAllPostSeasonActivitiesAction({ params: queryParams, endPoint: query }));
    }, [dispatch, selectedZoneId, selectedRegionId, selectedAreaId, selectedDistrictId, selectedSeasonId]);

    
  return (
    <Box sx={{ minHeight: '100vh', p:2 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2,  }}>
        Post Activity
      </Typography>

      {/* <Box sx={{ width: '100%', mb: 3 }}>
        <Grid container spacing={{ xs: 1, sm: 1, md: 1 }} sx={{ width: '100%', m: 0 }}>
          {stats.map((s) => (
            <Grid item size={{ xs: 6, sm: 3, md: 3 }} key={s.label} sx={{ p: 0, display: 'flex', justifyContent: 'stretch' }}>
              <Card sx={{ p: isIpad ? 1.5 : 2, borderRadius: 3, height: isIpad ? 60 : 50, width: '100%', mx: 0, display: 'flex', alignItems: 'center' }}>
                <Box display="flex" alignItems="center" width="100%" justifyContent="space-between">
                  <Box display="flex" flexDirection="column" alignItems="flex-start" gap={0.5}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.85rem', sm: 'inherit' }, textAlign: 'left', mb: 0 }}>
                      {s.label}
                    </Typography>
                    <Typography variant="h5" fontWeight={600} sx={{ fontSize: { xs: '1.1rem', sm: isIpad ? '1.4rem' : '1.5rem' }, textAlign: 'left', mb: 0 }}>
                      {s.value}
                    </Typography>
                  </Box>
                  <Box sx={{ bgcolor: s.bg, width: isIpad ? 44 : 45, height: isIpad ? 44 : 45, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 2 }}>
                    {s.icon}
                  </Box>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box> */}

      <Card sx={{ mr: { xs: 1 }, p: 2, borderRadius: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item size={{ xs: 12, sm: 4, md: 2 }}>
            <TextField
              size="small"
              placeholder="Search ..."
              slotProps={{ input: { startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} /> } }}
              fullWidth
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
            />
          </Grid>

          <Grid item size={{ xs: 6, sm: 4, md: 2 }}>
            <Select
              size="small"
              value={locationType}
              onChange={(e) => {
                const val = e.target.value;
                setLocationType(val);
                if (val !== 'District') setDistrictId('All');
                if (val !== 'Mandal') setMandalName('All');
              }}
              sx={{ width: '100%', borderRadius: 3 }}
            >
              <MenuItem value="All">Location Type</MenuItem>
              <MenuItem value="District">District</MenuItem>
              <MenuItem value="Mandal">Mandal</MenuItem>
            </Select>
          </Grid>

          <Grid item size={{ xs: 6, sm: 4, md: 2 }}>
            <Select size="small" value={districtId} onChange={(e) => setDistrictId(e.target.value)} sx={{ width: '100%', borderRadius: 3 }}>
              <MenuItem value="All">District</MenuItem>
              {District.map((d) => (
                <MenuItem key={d.districtId} value={d.districtId}>{d.name}</MenuItem>
              ))}
            </Select>
          </Grid>

          <Grid item size={{ xs: 6, sm: 4, md: 2 }}>
            <Select size="small" value={zoneId} onChange={(e) => setZoneId(e.target.value)} sx={{ width: '100%', borderRadius: 3 }}>
              <MenuItem value="All">Zone</MenuItem>
              {Zones.map((z) => (
                <MenuItem key={z.zoneId} value={z.zoneId}>{z.zoneName}</MenuItem>
              ))}
            </Select>
          </Grid>

          <Grid item size={{ xs: 6, sm: 4, md: 2 }}>
            <Select size="small" value={regionId} onChange={(e) => setRegionId(e.target.value)} sx={{ width: '100%', borderRadius: 3 }}>
              <MenuItem value="All">Region</MenuItem>
              {Regions.map((r) => (
                <MenuItem key={r.regionId} value={r.regionId}>{r.regionName}</MenuItem>
              ))}
            </Select>
          </Grid>

          <Grid item size={{ xs: 6, sm: 4, md: 2 }}>
            <Select size="small" value={areaId} onChange={(e) => setAreaId(e.target.value)} sx={{ width: '100%', borderRadius: 3 }}>
              <MenuItem value="All">Area</MenuItem>
              {Areas.map((a) => (
                <MenuItem key={a.areaId} value={a.areaId}>{a.areaName}</MenuItem>
              ))}
            </Select>
          </Grid>

          <Grid item size={{ xs: 6, sm: 4, md: 2 }}>
            <Select size="small" value={seasonId} onChange={(e) => setSeasonId(e.target.value)} sx={{ width: '100%', borderRadius: 3 }}>
              <MenuItem value="All">All Seasons</MenuItem>
              {Seasons.map((s) => (
                <MenuItem key={s.seasonId} value={s.seasonId}>{s.name}</MenuItem>
              ))}
            </Select>
          </Grid>

          <Grid item size={{ xs: 12, sm: 4, md: 2 }}>
            <Button
              variant="contained"
              sx={{
                borderRadius: 2,
                whiteSpace: 'nowrap',
                fontSize: { xs: '0.70rem', sm: '0.70rem', md: '0.85rem', lg: '1rem' },
                '@media (min-width:1100px) and (max-width:1500px)': { fontSize: '0.70rem' }
              }}
              fullWidth
              onClick={handleAddTarget}
            >
              + Add New Target
            </Button>
          </Grid>
        </Grid>
      </Card>

      <Box sx={{ mt: 3 }}>
        {isMobile ? (
          <Grid container spacing={2}>
            {pagedData.map((row) => (
              <Grid item size={{ xs: 12 }} key={row.id ?? row.seasonId}>
                <Paper
                  elevation={0}
                  sx={{ borderRadius: 3, p: 1.5, border: '1px solid #E5E7EB', '&:hover': { border: '2px solid #2563EB', boxShadow: '0 4px 14px rgba(37,99,235,0.18)' } }}
                >
                  <Box mb={1} display="flex" alignItems="center" gap={1.25}>
                    <Typography fontWeight={600} minWidth={88}>Season:</Typography>
                    <Typography variant="body2">{getSeasonDisplayName(row)}</Typography>
                    <Box ml="auto">
                      <Chip label={row.is_active ? 'Active' : 'Inactive'} size="small" sx={{ backgroundColor: row.is_active ? '#D1FAE5' : '#FEE2E2', height: 24, '& .MuiChip-label': { fontSize: 11, fontWeight: 600 } }} />
                    </Box>
                  </Box>

                  <Box mb={1} display="flex" alignItems="center" gap={1.25}>
                    <Typography fontWeight={600} minWidth={88}>Area Type:</Typography>
                    <Typography variant="body2">{row?.target_area_type}</Typography>
                  </Box>
                  <Box mb={1} display="flex" alignItems="center" gap={1.25}>
                    <Typography fontWeight={600} minWidth={88}>Area Name:</Typography>
                    <Typography variant="body2">{getAreaName(row)}</Typography>
                  </Box>

                  <Box mb={1} display="flex" alignItems="center" gap={1.25}>
                    <Typography fontWeight={600} minWidth={88}>DV Count:</Typography>
                    <Typography variant="body2">{row?.dv_done_count ?? 0}/{row?.dv_count ?? 0}</Typography>
                  </Box>

                  <Box mb={1} display="flex" alignItems="center" gap={1.25}>
                    <Typography fontWeight={600} minWidth={88}>FD Count:</Typography>
                    <Typography variant="body2">{row?.fd_done_count ?? 0}/{row?.fd_count ?? 0}</Typography>
                  </Box>

                  <Box mb={1} display="flex" alignItems="center" gap={1.25}>
                    <Typography fontWeight={600} minWidth={88}>FV Count:</Typography>
                    <Typography variant="body2">{row?.fv_done_count ?? 0}/{row?.fv_count ?? 0}</Typography>
                  </Box>

                  <Box display="flex" alignItems="center" justifyContent="flex-end" gap={1} mt={1}>
                    <IconButton size="small" aria-label="view" onClick={() => handleView(row)}>
                      <Visibility fontSize="small" sx={{ color: '#036144', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleEdit(row)}>
                      <EditOutlinedIcon fontSize="small" sx={{ color: '#2563EB', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
                    </IconButton>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box display="flex" flexDirection="column" gap={1}>
            <Box
              elevation={0}
              sx={{ display: { xs: 'none', sm: 'grid' }, gridTemplateColumns: { sm: gridColsDesktop }, alignItems: 'center', px: 2, py: 2, columnGap: 0 }}
            >
              {['Season ID', 'Area Type', 'Area Name', 'DV Count', 'FD Count', 'FV Count', 'Status', 'Actions'].map((h) => (
                <Typography key={h} variant="caption" fontWeight={600} sx={{ textTransform: 'uppercase', textAlign: h === 'Actions' ? 'right' : 'left' }}>
                  {h}
                </Typography>
              ))}
            </Box>

            {pagedData.map((row) => (
              <Paper
                key={row.id ?? row.seasonId}
                elevation={0}
                sx={{ borderRadius: 3, display: { xs: 'none', sm: 'grid' }, gridTemplateColumns: { sm: gridColsDesktop }, alignItems: 'center', border: '1px solid #E5E7EB', px: 1.2, py: 1, gap: 0.5, columnGap: 0.5, '&:hover': { border: '2px solid #2563EB', boxShadow: '0 4px 14px rgba(37,99,235,0.18)' } }}
              >
                <Typography variant="body2">{getSeasonDisplayName(row)}</Typography>
                <Typography variant="body2">{row.target_area_type}</Typography>
                <Typography variant="body2">{getAreaName(row)}</Typography>
                <Typography variant="body2">{row.dv_done_count ?? 0}/{row.dv_count ?? 0}</Typography>
                <Typography variant="body2">{row.fd_done_count ?? 0}/{row.fd_count ?? 0}</Typography>
                <Typography variant="body2">{row.fv_done_count ?? 0}/{row.fv_count ?? 0}</Typography>
                <Chip label={row.is_active ? 'Active' : 'Inactive'} size="small" sx={{ width: 90, backgroundColor: row.is_active ? '#D1FAE5' : '#FEE2E2', ml: -2, height: 24, '& .MuiChip-label': { fontSize: 12, fontWeight: 600 } }} />
                <Box display="flex" alignItems="center" gap={1} justifyContent="flex-end">
                  <IconButton size="small" aria-label="view" onClick={() => handleView(row)}>
                    <Visibility fontSize="small" sx={{ color: '#036144', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
                  </IconButton>
                  <IconButton size="small" aria-label="edit" onClick={() => handleEdit(row)}>
                    <EditOutlinedIcon fontSize="small" sx={{ color: '#2563EB', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
                  </IconButton>
                </Box>
              </Paper>
            ))}
          </Box>
        )}
        {totalPages > 1 && (
          <Box display="flex" alignItems="center" justifyContent="space-between" mt={2} flexWrap="wrap" gap={1}>
            <Typography variant="body2" >
              Showing {Math.min((page - 1) * PAGE_SIZE + 1, filteredData.length)} to {Math.min(page * PAGE_SIZE, filteredData.length)} of {filteredData.length} entries
            </Typography>
            <Pagination count={totalPages} page={page} onChange={(_, val) => setPage(val)} color="primary" />
          </Box>
        )}
      </Box>

      <PostActivityDetailsView open={openViewDialog} onClose={handleCloseViewDialog} row={viewRow} />
    </Box>
  );
};

export default PostActivityAMRM;
